import React, {useEffect, useMemo, useRef, useState} from 'react';
import config from "../../../../../config";
import {saveHotkeysConfig} from "../../../../../services/company/api";
import Translate from "../../../../../components/Translate";
import {t} from "../../../../../components/Translate";
import BlueButton from "../../../../../components-new/buttons/BlueButton";
import SearchInput from "../../../../../components-new/form/SearchInput";
import ResetButton from "../../../../../components-new/buttons/ResetButton";
import Colors from "../../../../../utils/Colors";


const styles2 = {
    container: {
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flex: '1 1 auto',
        minHeight: 0,
        overflow: 'hidden',
    },
    panel: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
    },
    rows: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        paddingRight: '4px',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '14px',
        padding: '12px 14px',
        borderRadius: '12px',
        backgroundColor: 'var(--ds-bg-primary)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: Colors.silverBlueExtraLite,
        transition: 'all .2s ease',
    },
    rowEditing: {
        borderColor: Colors.blueLite,
        boxShadow: `0 0 0 3px ${Colors.blueExtraLite}`,
        backgroundColor: Colors.blueExtraLite2,
    },
    labelWrap: {
        display: 'flex',
        alignItems: 'center',
        minWidth: '220px',
    },
    label: {
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: 500,
        color: Colors.silverBlueDark,
    },
    hotkeyBox: {
        minWidth: '180px',
        padding: '8px 12px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: Colors.silverBlueSemiLite,
        borderRadius: '10px',
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: 'center',
        color: Colors.blueDark,
        fontSize: '14px',
        fontWeight: 700,
        letterSpacing: '.4px',
        transition: 'all .2s ease',
    },
    editing: {
        borderColor: Colors.blueLite,
        backgroundColor: 'var(--ds-bg-primary)',
        color: Colors.blue,
        boxShadow: `0 0 0 3px ${Colors.blueExtraLite}`,
    },
    footer: {
        textAlign: 'end',
        marginTop: '18px',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        marginBottom: '18px',
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    emptyState: {
        textAlign: 'center',
        color: Colors.silverBlueDark,
        fontSize: '14px',
        padding: '24px 10px',
    },
};

const reservedHotkeys = [
    'Ctrl+T', 'Ctrl+W', 'Ctrl+N',
    'Ctrl+Shift+I', 'F12', 'Ctrl+Shift+R', 'Ctrl+C', 'Ctrl+V'
];

const Hotkeys = ({hotkeys = [], setHotkeys, addNotification, styles, user}) => {
    const [localHotkeys, setLocalHotkeys] = useState([]);
    const [savedHotkeys, setSavedHotkeys] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [error, setError] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const containerRef = useRef(null);

    useEffect(() => {
        setLocalHotkeys(hotkeys);
        if (savedHotkeys.length === 0 && hotkeys.length > 0) {
            setSavedHotkeys(hotkeys.map(hk => ({...hk})));
        }
    }, [hotkeys, savedHotkeys.length]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (editingIndex === null) return;

            e.preventDefault();

            const keys = [];
            if (e.ctrlKey) keys.push('Ctrl');
            if (e.shiftKey) keys.push('Shift');
            if (e.altKey) keys.push('Alt');
            if (e.metaKey) keys.push('Meta');

            const key = e.code === 'Space' ? 'Space' : (e.key.length === 1 ? e.key.toUpperCase() : e.key);
            if (!['CONTROL', 'SHIFT', 'ALT', 'META'].includes(key.toUpperCase())) {
                keys.push(key);
            }

            const combo = keys.join('+');

            if (reservedHotkeys.includes(combo)) {
                setError("hotkeyReservedError");
                addNotification('hotkeyReservedError', config.notificationLevels.error);
            } else {
                setError('');
                const updated = [...localHotkeys];
                updated[editingIndex].hotkey = combo;
                setLocalHotkeys(updated);
                setHotkeys(updated);
            }
        };

        window.addEventListener('keydown', handleKeyDown, {capture: true});
        return () => window.removeEventListener('keydown', handleKeyDown, {capture: true});
    }, [editingIndex, addNotification, localHotkeys, setHotkeys]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!containerRef.current) return;

            const clickedHotkeyBox = e.target.closest('[data-hotkey-box="true"]');
            const clickedInsideComponent = containerRef.current.contains(e.target);

            if (!clickedInsideComponent || !clickedHotkeyBox) {
                setEditingIndex(null);
                setError('');
            }
        };

        window.addEventListener('mousedown', handleClickOutside);
        return () => window.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSaveAll = async () => {
        const usedHotkeys = new Set();
        for (let hk of localHotkeys) {
            if (hk.hotkey.length === 1) {
                addNotification('hotkeyMustIncludeModifier', config.notificationLevels.error);
                return
            }
            if (!hk.hotkey) {
                addNotification('someHotkeysAreEmpty', config.notificationLevels.error);
                return;
            }
            if (reservedHotkeys.includes(hk.hotkey)) {
                addNotification(`hotkeyReservedError`, config.notificationLevels.error);
                return;
            }
            if (usedHotkeys.has(hk.hotkey)) {
                addNotification('duplicateHotkeyFound', config.notificationLevels.error);
                return;
            }
            usedHotkeys.add(hk.hotkey);
        }

        try {
            const requests = localHotkeys.map(({action, hotkey}) => ({action, hotkey}));
            await saveHotkeysConfig(requests);
            setSavedHotkeys(localHotkeys.map(hk => ({...hk})));
            addNotification('saved', config.notificationLevels.success);
        } catch (err) {
            console.error(err);
            addNotification('somethingWentWrong', config.notificationLevels.error);
        }
    };

    const resetChangedHotkeys = () => {
        const resetData = savedHotkeys.map(hk => ({...hk}));
        setLocalHotkeys(resetData);
        setHotkeys(resetData);
        setEditingIndex(null);
        setError('');
    };

    const filteredHotkeys = localHotkeys
        .map((hk, index) => ({hk, index}))
        .filter(({hk}) => {
            const search = searchValue.trim().toLowerCase();
            if (!search) return true;

            const translatedAction = (
                user && user.language
                    ? (t({user, text: hk.action}) || hk.action || '')
                    : (hk.action || '')
            ).toLowerCase();
            const action = (hk.action || '').toLowerCase();
            const hotkey = (hk.hotkey || '').toLowerCase();
            return translatedAction.includes(search) || action.includes(search) || hotkey.includes(search);
        });

    const hasHotkeyChanges = useMemo(() => {
        if (localHotkeys.length !== savedHotkeys.length) return true;

        for (let i = 0; i < localHotkeys.length; i++) {
            if (localHotkeys[i].action !== savedHotkeys[i].action) return true;
            if (localHotkeys[i].hotkey !== savedHotkeys[i].hotkey) return true;
        }

        return false;
    }, [localHotkeys, savedHotkeys]);

    return (
        <div style={{...styles.fullWidthContainer, minHeight: 0, height: '100%', overflow: 'hidden'}} ref={containerRef}>
            <div style={{...styles.fullWidthCard, maxHeight: '100%', height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                <div style={styles2.toolbar}>
                    <div style={styles2.toolbarLeft}>
                        <SearchInput
                            value={searchValue}
                            onChange={e => setSearchValue(e.target.value)}
                            placeholder="search"
                            style={{
                                width: '320px',
                                height: '36px',
                            }}
                        />
                    </div>
                    <ResetButton
                        onClick={resetChangedHotkeys}
                        width={150}
                        height={36}
                        disabled={!hasHotkeyChanges}
                        showText={false}
                    >
                        <Translate text={'reset'}/>
                    </ResetButton>
                </div>

                <div style={styles2.container}>
                    <div style={styles2.panel}>
                        <div style={styles2.rows}>
                            {filteredHotkeys.length === 0 ? (
                                <div style={styles2.emptyState}>
                                    <Translate text={'noResultsFound'}/>
                                </div>
                            ) : (
                                filteredHotkeys.map(({hk, index}) => (
                                    <div
                                        key={index}
                                        style={{
                                            ...styles2.row,
                                            ...(editingIndex === index ? styles2.rowEditing : {}),
                                        }}
                                    >
                                        <div style={styles2.labelWrap}>
                                            <div style={styles2.label}><Translate text={hk.action}/></div>
                                        </div>
                                        <div
                                            data-hotkey-box="true"
                                            style={{...styles2.hotkeyBox, ...(editingIndex === index ? styles2.editing : {})}}
                                            onClick={() => {
                                                setEditingIndex(editingIndex === index ? null : index);
                                                setError('');
                                            }}
                                        >
                                            {hk.hotkey}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
                <div style={styles2.footer}>
                    <BlueButton
                        width={'200px'}
                        height={'40px'}
                        onClick={handleSaveAll}
                        disabled={!!error}
                    >
                        <Translate text={'save'}/>
                    </BlueButton>
                </div>

            </div>
        </div>
    );
};

export default Hotkeys;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/my-history/components/Hotkeys.jsx